/**
 * Renders ticket message content preserving the author's line breaks
 * (Enter-separated paragraphs) and applying the correct text direction
 * per line: lines that start with an English/Latin letter are LTR, lines
 * that start with a Persian/Arabic letter are RTL.
 *
 * Each line is rendered as a block-level element so both `direction` and
 * `text-align` take effect (inline spans cannot be independently aligned
 * inside an RTL parent).
 */

// Arabic script blocks (includes Persian) + ZWNJ/ZWNJ-adjacent chars
const PERSIAN_CHAR = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF\u200C\u200D]/;
const LATIN_CHAR = /[A-Za-z]/;

export function detectLineDirection(line: string): "rtl" | "ltr" {
  const trimmed = line.trim();
  if (!trimmed) return "rtl"; // app is Persian-first; empty/spacing lines default RTL

  const firstChar = trimmed.charAt(0);
  if (PERSIAN_CHAR.test(firstChar)) return "rtl";
  if (LATIN_CHAR.test(firstChar)) return "ltr";

  // First char is a digit/symbol/punctuation — look ahead for the first
  // letter so e.g. "12345 error message" renders LTR.
  const firstLetter = trimmed.slice(0, 12).match(LATIN_CHAR) ?? trimmed.slice(0, 12).match(PERSIAN_CHAR);
  if (firstLetter) {
    return LATIN_CHAR.test(firstLetter[0]) ? "ltr" : "rtl";
  }
  return "rtl";
}

export default function TicketMessageContent({ content }: { content: string }) {
  const lines = content.split(/\r?\n/);
  return (
    <div className="break-words">
      {lines.map((line, i) => {
        const dir = detectLineDirection(line);
        return (
          <div
            key={i}
            dir={dir}
            style={{
              direction: dir,
              textAlign: dir === "ltr" ? "left" : "right",
              unicodeBidi: "isolate",
              whiteSpace: "pre-wrap",
            }}
          >
            {line || "\u00A0"}
          </div>
        );
      })}
    </div>
  );
}
